import React from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../../../context/I18nContext';
import { IconEdit, IconTrash, IconEye, IconUsers, IconClock, IconRepeat } from '../../../components/icons';

/**
 * Format recurrence type to human-readable string
 */
const formatRecurrence = (recurrence, t) => {
    if (!recurrence) return '';
    const { type, interval = 1 } = recurrence;
    
    switch (type) {
        case 'once':
            return t('recurrence.once', { ns: 'reminders' });
        case 'daily':
            return interval === 1 
                ? t('recurrence.daily', { ns: 'reminders' })
                : t('recurrence.everyNDays', { ns: 'reminders', count: interval });
        case 'weekly':
            return interval === 1
                ? t('recurrence.weekly', { ns: 'reminders' })
                : t('recurrence.everyNWeeks', { ns: 'reminders', count: interval });
        case 'biweekly':
            return t('recurrence.biweekly', { ns: 'reminders' });
        case 'monthly':
            return interval === 1
                ? t('recurrence.monthly', { ns: 'reminders' })
                : t('recurrence.everyNMonths', { ns: 'reminders', count: interval });
        case 'quarterly':
            return t('recurrence.quarterly', { ns: 'reminders' });
        case 'yearly':
            return t('recurrence.yearly', { ns: 'reminders' });
        default:
            return t('recurrence.custom', { ns: 'reminders' });
    }
};

/**
 * Format date to local string
 */
const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

/**
 * Get priority color classes
 */
const getPriorityClasses = (priority) => {
    switch (priority) {
        case 'urgent':
            return 'bg-red-100 text-red-800 border-red-200';
        case 'high':
            return 'bg-orange-100 text-orange-800 border-orange-200';
        case 'medium':
            return 'bg-blue-100 text-blue-800 border-blue-200';
        case 'low':
            return 'bg-gray-100 text-gray-800 border-gray-200';
        default:
            return 'bg-gray-100 text-gray-800 border-gray-200';
    }
};

/**
 * Get status color classes
 */
const getStatusClasses = (status) => {
    switch (status) {
        case 'active':
            return 'bg-green-100 text-green-800';
        case 'paused':
            return 'bg-yellow-100 text-yellow-800';
        case 'completed':
            return 'bg-blue-100 text-blue-800';
        case 'archived':
            return 'bg-gray-100 text-gray-600';
        default:
            return 'bg-gray-100 text-gray-600';
    }
};

const ReminderCard = ({ reminder, onDelete }) => {
    const { t } = useI18n(['common', 'reminders']);
    
    const {
        _id,
        title,
        description,
        icon,
        color,
        category,
        priority,
        status,
        schedule,
        collaborators,
        isOwner,
        myPermission,
        tags
    } = reminder;

    const canEdit = myPermission === 'owner' || myPermission === 'write';
    const canDelete = myPermission === 'owner';

    return (
        <div 
            className={`bg-white rounded-lg shadow-sm border-l-4 hover:shadow-md transition-shadow ${
                !isOwner ? 'ring-1 ring-purple-200' : ''
            }`}
            style={{ borderLeftColor: color || '#3B82F6' }}
        >
            <div className="p-4">
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <span className="text-2xl">{icon || '🔔'}</span>
                        <div>
                            <h3 className="font-semibold text-gray-900 line-clamp-1">{title}</h3>
                            {category && (
                                <span className="text-xs text-gray-500">{category}</span>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-1">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusClasses(status)}`}>
                            {t(`status.${status}`, { ns: 'reminders' })}
                        </span>
                    </div>
                </div>

                {/* Description */}
                {description && (
                    <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                        {description}
                    </p>
                )}

                {/* Schedule Info */}
                <div className="flex flex-wrap gap-2 mb-3 text-xs">
                    {/* Next occurrence */}
                    {schedule?.nextOccurrence && (
                        <div className="flex items-center gap-1 text-gray-600 bg-gray-50 px-2 py-1 rounded">
                            <IconClock className="w-3 h-3" />
                            <span>{formatDate(schedule.nextOccurrence)}</span>
                        </div>
                    )}
                    
                    {/* Recurrence */}
                    {schedule?.recurrence && (
                        <div className="flex items-center gap-1 text-gray-600 bg-gray-50 px-2 py-1 rounded">
                            <IconRepeat className="w-3 h-3" />
                            <span>{formatRecurrence(schedule.recurrence, t)}</span>
                        </div>
                    )}

                    {/* Priority */}
                    <span className={`px-2 py-0.5 rounded border ${getPriorityClasses(priority)}`}>
                        {t(`priority.${priority}`, { ns: 'reminders' })}
                    </span>
                </div>

                {/* Tags */}
                {tags && tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                        {tags.slice(0, 3).map(tag => (
                            <span key={tag} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
                                #{tag}
                            </span>
                        ))}
                        {tags.length > 3 && (
                            <span className="text-xs text-gray-500">+{tags.length - 3}</span>
                        )}
                    </div>
                )}

                {/* Footer */}
                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    {/* Ownership / Collaborators */}
                    <div className="flex items-center gap-2">
                        {!isOwner && (
                            <span className="inline-flex items-center gap-1 text-xs text-purple-600 bg-purple-50 px-2 py-1 rounded">
                                <IconUsers className="w-3 h-3" />
                                {t('sharedWithYou', { ns: 'reminders' })}
                            </span>
                        )}
                        {isOwner && collaborators && collaborators.length > 0 && (
                            <span className="inline-flex items-center gap-1 text-xs text-gray-600">
                                <IconUsers className="w-3 h-3" />
                                {collaborators.length} {t('collaborators', { ns: 'reminders' })}
                            </span>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1">
                        <Link
                            to={`/reminders/view/${_id}`}
                            className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                            title={t('view', { ns: 'common' })}
                        >
                            <IconEye className="w-4 h-4" />
                        </Link>
                        
                        {canEdit && (
                            <Link
                                to={`/reminders/edit/${_id}`}
                                className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded"
                                title={t('edit', { ns: 'common' })}
                            >
                                <IconEdit className="w-4 h-4" />
                            </Link>
                        )}
                        
                        {canDelete && (
                            <button
                                onClick={() => onDelete(_id)}
                                className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                                title={t('delete', { ns: 'common' })}
                            >
                                <IconTrash className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReminderCard;
